import { useState, useCallback } from 'react';
import axios from 'axios';
import { message } from 'antd';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_BASE = `${API_BASE_URL}/api`;

export const useTranslation = () => {
  const [translating, setTranslating] = useState(false);
  const [autoTranslate, setAutoTranslate] = useState(false);

  /**
   * Translate a single text
   */
  const translateText = useCallback(async (text, targetLanguage = 'mr', sourceLanguage = 'en') => {
    if (!text || text.trim() === '') return text;
    
    try {
      const response = await axios.post(`${API_BASE}/translate/text`, {
        text,
        targetLanguage,
        sourceLanguage
      });
      
      return response.data.translatedText;
    } catch (error) {
      console.error('Translation error:', error);
      return text;
    }
  }, []);

  /**
   * Translate multiple texts
   */
  const translateBatch = useCallback(async (texts, targetLanguage = 'mr') => {
    if (!texts || texts.length === 0) return texts;
    
    try {
      const response = await axios.post(`${API_BASE}/translate/batch`, {
        texts,
        targetLanguage
      });
      
      return response.data.translations;
    } catch (error) {
      console.error('Batch translation error:', error);
      return texts;
    }
  }, []);

  /**
   * Translate a form field
   */
  const translateField = useCallback(async (field) => {
    if (!field) return field;
    
    try {
      setTranslating(true);
      const response = await axios.post(`${API_BASE}/translate/field`, {
        field,
        targetLanguage: 'mr'
      });
      
      return response.data.field;
    } catch (error) {
      console.error('Field translation error:', error);
      message.error('Failed to translate field');
      return field;
    } finally {
      setTranslating(false);
    }
  }, []);

  /**
   * Translate an entire form (array of fields)
   */
  const translateForm = useCallback(async (fields) => {
    if (!fields || fields.length === 0) return fields;
    
    try {
      setTranslating(true);
      const response = await axios.post(`${API_BASE}/translate/form`, {
        fields,
        targetLanguage: 'mr'
      });
      
      message.success('Form translated successfully');
      return response.data.fields;
    } catch (error) {
      console.error('Form translation error:', error);
      message.error('Failed to translate form');
      return fields;
    } finally {
      setTranslating(false);
    }
  }, []);

  /**
   * Translate while typing (debounced)
   */
  const translateTyping = useCallback(async (text, targetLanguage = 'mr') => {
    if (!text || text.trim() === '' || !autoTranslate) return text;
    
    try {
      const response = await axios.post(`${API_BASE}/translate/text`, {
        text,
        targetLanguage
      });
      
      return response.data.translatedText;
    } catch (error) {
      console.error('Typing translation error:', error);
      return text;
    }
  }, [autoTranslate]);

  /**
   * Check translation service status
   */
  const checkTranslationStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE}/translate/status`);
      return response.data;
    } catch (error) {
      console.error('Status check error:', error);
      return { initialized: false };
    }
  }, []);

  return {
    translateText,
    translateBatch,
    translateField,
    translateForm,
    translateTyping,
    checkTranslationStatus,
    translating,
    autoTranslate,
    setAutoTranslate
  };
};